// Global Instructions Rule Applied!
//
// Standalone multi-fund Investment Detail grid. Parallel to
// FundInvestmentDetailTable (the per-fund Investments tab) — same layout,
// Aggregations builder, ViewConfig, totals and export — but it is a
// top-level grid (primary toolbar colour, like the Funds tablegrid) and the
// fund scope + as-of date live in the Filtering drawer's Options panel
// (no top-level selectors). Default aggregation: per Fund, per Company.

import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Row, Col } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type FieldConfig } from '../../../core/components/InputManager/types.ts'
import type { RootState } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../state_manager/utils/reportingGridExcelExport.ts'

import TableView from '../../../core/components/TableView/TableView.tsx'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_ACCOUNTS } from '../../../state_manager/globals.ts'
import { recordHasTotalLabel, stripTotalTextFromCellNode } from '../../../core/utils/recordRowBold.ts'

import { useInvestmentDetail } from '../hooks/useInvestmentDetail.ts'
import { useInvestmentDetailReportId } from '../../../state_manager/hooks/useInvestmentDetailReportId.ts'
import { useFreezeLeadingColumns } from '../../../state_manager/hooks/useFreezeLeadingColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import {
  getReportingGridColumnsForReportId,
  reportingColumnToViewConfigColumn
} from '../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../state_manager/utils/reportingColumnCatalog.ts'
import { fetchInvestmentDetailFiltered } from '../../funds/services/fundsService.ts'
import { AggregationsButton } from '../../funds/components/aggregations/AggregationsButton.tsx'
import { resolveDimLabelColumn } from '../../funds/components/aggregations/aggregationDimensions.ts'
import { FUND_INVESTMENT_DIM_LABEL_FIELD_NAMES } from '../../funds/components/aggregations/aggregationDimensions.ts'
import { InvestmentsOptionsPanel } from './InvestmentsOptionsPanel.tsx'
import { INVESTMENT_AGGREGATION_DIMENSIONS } from './investmentAggregationDimensions.ts'

interface InvestmentDetailTableProps {
  /** Embedded mode: preset fund scope + no preference read/write. */
  presetFundIds?: string[]
}

export function InvestmentDetailTable({ presetFundIds }: InvestmentDetailTableProps = {}): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()

  const reportId = useInvestmentDetailReportId()
  const freezeLeadingColumns = useFreezeLeadingColumns(reportId)
  const reportingConfigs = useSelector((s: RootState) => s.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector((s: RootState) => s.globalConfigs?.reporting_column_defaults ?? [])
  const reportingColumns = useSelector((s: RootState) => s.globalConfigs?.reporting_columns ?? [])
  const modules = useSelector((s: RootState) => s.globalConfigs?.modules ?? [])

  const {
    data,
    summary,
    loading,
    filterOptions,
    filterOptionsLoading,
    activeAggregation,
    activeFilters,
    activeSorting,
    activeOptions,
    draftOptions,
    hasMore,
    currentPage,
    loadingMore,
    handleLoadMore,
    preferenceRow,
    handleSetAggregation,
    handleSetFilters,
    handleSetSorting,
    handleSetDraftOptions,
    handleApplyOptions,
    handleCancelOptions,
    handleResetOptionsToDefault,
    handleResetAll,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableAggregation,
    defaultTableFilters,
    defaultTableSorting,
    defaultTableOptions
  } = useInvestmentDetail(presetFundIds)

  const sameFundIds = (a: string[], b: string[]): boolean =>
    a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|')

  const optionsIsDirty = useMemo(() => {
    return draftOptions.datetime !== activeOptions.datetime
        || draftOptions.includeWarehoused !== activeOptions.includeWarehoused
        || draftOptions.includeConvertedOut !== activeOptions.includeConvertedOut
        || draftOptions.includeIrr !== activeOptions.includeIrr
        || !sameFundIds(draftOptions.fundIds, activeOptions.fundIds)
  }, [draftOptions, activeOptions])

  const optionsAlreadyDefault = useMemo(() => {
    return activeOptions.datetime === defaultTableOptions.datetime
        && activeOptions.includeWarehoused === defaultTableOptions.includeWarehoused
        && activeOptions.includeConvertedOut === defaultTableOptions.includeConvertedOut
        && activeOptions.includeIrr === defaultTableOptions.includeIrr
        && sameFundIds(activeOptions.fundIds, defaultTableOptions.fundIds)
  }, [activeOptions, defaultTableOptions])

  const baseGridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)

  const COMPANY_WIDTH = 154
  const TRANCHE_WIDTH = 246

  const applyOverrides = useCallback(
    (col: ViewConfigColumn): ViewConfigColumn => {
      if (col.valueKey === 'accounts__name') {
        return { ...col, columnTitle: 'Company', minWidth: COMPANY_WIDTH }
      }
      if (col.valueKey === 'tranches__name') {
        return { ...col, minWidth: TRANCHE_WIDTH }
      }
      return col
    },
    []
  )

  // Auto-inject the dim-label column for each active aggregation dim that
  // isn't already in the user's visible columns.
  const gridColumns = useMemo<ViewConfigColumn[]>(() => {
    const present = new Set(baseGridColumns.map((c) => c.valueKey))
    const injected: ViewConfigColumn[] = []
    for (const dim of activeAggregation) {
      const col = resolveDimLabelColumn(dim, reportingColumnDefaults as any[], modules as any[])
      if (!col) continue
      const fn = (col as any).field_name as string
      if (!fn || present.has(fn)) continue
      injected.push(applyOverrides(reportingColumnToViewConfigColumn(col as any, undefined, true)))
      present.add(fn)
    }

    const baseWithOverrides = baseGridColumns.map(applyOverrides)
    if (injected.length === 0) return baseWithOverrides

    const companyIdx = baseWithOverrides.findIndex(
      (c) => c.valueKey === 'accounts__name'
    )
    if (companyIdx === -1) {
      return [...injected, ...baseWithOverrides]
    }
    return [
      ...baseWithOverrides.slice(0, companyIdx + 1),
      ...injected,
      ...baseWithOverrides.slice(companyIdx + 1)
    ]
  }, [baseGridColumns, activeAggregation, reportingColumnDefaults, modules, applyOverrides])

  const populatedDimFieldNames = useMemo<Set<string> | null>(() => {
    const nonTotalRows = data.filter((row) => !recordHasTotalLabel(row))
    if (nonTotalRows.length === 0) return null
    const populated = new Set<string>()
    for (const fn of FUND_INVESTMENT_DIM_LABEL_FIELD_NAMES) {
      const found = nonTotalRows.some((row) => {
        const v = (row as Record<string, unknown>)[fn]
        return typeof v === 'string' ? v.length > 0 : v != null
      })
      if (found) populated.add(fn)
    }
    return populated
  }, [data])

  const visibleGridColumns = useMemo<ViewConfigColumn[]>(() => {
    if (!populatedDimFieldNames) return gridColumns
    return gridColumns.filter((col) => {
      if (!FUND_INVESTMENT_DIM_LABEL_FIELD_NAMES.has(col.valueKey)) return true
      return populatedDimFieldNames.has(col.valueKey)
    })
  }, [gridColumns, populatedDimFieldNames])

  const viewConfigColumns: ViewConfigColumn[] = gridColumns

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'funds' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  const { columns: baseColumns, frozenColumns } = useMemo(
    () => createTableColumns({ columns: visibleGridColumns, freezeLeadingColumns, fallbackRecordModuleKey: 'companies' }),
    [visibleGridColumns, freezeLeadingColumns]
  )

  const openRecordFullColumns = useMemo(
    () =>
      new Set(
        visibleGridColumns
          .filter((c) => c.fieldKey === 'open-record-full')
          .map((c) => c.valueKey)
      ),
    [visibleGridColumns]
  )

  const firstColumnValueKey = visibleGridColumns[0]?.valueKey

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const prevRender = col.render
      if (!prevRender) return col
      const isFirstColumn = firstColumnValueKey != null && col.key === firstColumnValueKey
      const opensRecordFull = openRecordFullColumns.has(String(col.key))
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const isTotalRow = recordHasTotalLabel(record)
          if (isTotalRow && isFirstColumn) {
            return <span>Total</span>
          }
          const node = prevRender!(value, record, index, editingContext)
          const renderedNode = isTotalRow && !isFirstColumn ? stripTotalTextFromCellNode(node) : node

          if (!opensRecordFull || isTotalRow) return renderedNode
          const accountId = typeof record.accounts__id === 'string' ? record.accounts__id : ''
          if (!accountId) return renderedNode

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() =>
                openFormInTab(TABLE_ACCOUNTS, accountId, String(record.accounts__name ?? ''))
              }
            >
              {renderedNode}
            </button>
          )
        }
      }
    })
  }, [baseColumns, firstColumnValueKey, openRecordFullColumns, openFormInTab])

  const ROW_ID_KEYS = useMemo(
    () => [
      'tranches__id',
      'securities__id',
      'accounts__id',
      'cfg_security_types__id',
      'cfg_asset_valuation_type__id',
      'up_tranches__id',
      'vintage__id',
      'funds__id',
      'accounts__industry',
      'accounts__country',
      'accounts__city',
      'accounts__state',
      'currency_code'
    ],
    []
  )

  const dataSource = useMemo(
    () =>
      data.map((row, i) => {
        const isTotal = recordHasTotalLabel(row)
        if (isTotal) {
          const id = `total-${(row.currency_code as string) || i}`
          return { ...row, id, key: id }
        }
        const parts = ROW_ID_KEYS.map((k) => {
          const v = (row as Record<string, unknown>)[k]
          return typeof v === 'string' && v.length > 0 ? v : ''
        })
        const composite = parts.join('|')
        const id = composite.length > 0 ? composite : `row-${i}`
        return { ...row, id, key: id }
      }),
    [data, ROW_ID_KEYS]
  )

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  const [exportLoading, setExportLoading] = useState(false)
  const handleExport = useCallback(async () => {
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchInvestmentDetailFiltered(
        activeOptions.fundIds,
        activeAggregation,
        activeSorting,
        activeFilters,
        1,
        100000,
        token,
        {
          datetime: activeOptions.datetime,
          includeWarehoused: activeOptions.includeWarehoused,
          includeConvertedOut: activeOptions.includeConvertedOut,
          includeIrr: activeOptions.includeIrr
        }
      )
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      const records = res.data.filter((r: any) => !recordHasTotalLabel(r)) as Record<string, unknown>[]
      if (records.length === 0) {
        toast.info('No rows to export')
        return
      }
      const payload = buildReportingGridExcelPayload(visibleGridColumns, records)
      const filename = `Investment Detail - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [activeOptions, activeAggregation, activeSorting, activeFilters, visibleGridColumns])

  const summaryFields = useMemo<FieldConfig[]>(() => {
    const txt = (v: unknown): string => (v == null || v === '' ? '–' : String(v))
    const f = (key: string, label: string, value: unknown): FieldConfig =>
      ({ key, label, type: FieldTypeEnum.TEXT, value: txt(value), editable: false } as FieldConfig)
    return [
      f('investments', 'No. Investments', summary.investments_fmt),
      f('companies', 'No. Companies', summary.companies_fmt),
      f('no_active', 'No. Active', summary.no_active_fmt),
      f('no_exited', 'No. Exited', summary.no_exited_fmt)
    ]
  }, [summary])

  return (
    <div className='flex-1 min-h-0 h-full flex flex-col gap-2'>
      <div className='flex-shrink-0'>
        <FormSection title='Summary' titleColor='blue' scrollable={false}>
          <div className='p-3'>
            <Row gutter={24}>
              {summaryFields.map((field) => (
                <Col xs={24} sm={12} md={6} key={field.key}>
                  <InputManager
                    inputMode='read'
                    labelPosition='inline'
                    fields={[field]}
                    fieldWrapping={true}
                  />
                </Col>
              ))}
            </Row>
          </div>
        </FormSection>
      </div>
      <ViewToolbar
        moduleKey={TABLE_ACCOUNTS}
        recordTitleField='accounts__name'
        searchPlaceholder='Search Investments...'
        addButtonEnabled={false}
        variant='primary'
        leftSlot={
          <div className='flex items-center gap-2 text-white'>
            <span className='w-2 h-2 rounded-full bg-indigo-300' />
            <span className='font-semibold text-sm'>Investment Detail</span>
          </div>
        }
        activeFilters={activeFilters}
        onRefreshData={() => refetch()}
        onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
        defaultFilters={defaultTableFilters}
        onResetFilters={handleResetAll}
        exportEnabled
        onExport={handleExport}
        exportLoading={exportLoading}
        beforeAddSlot={
          <div className='flex items-center gap-2'>
            <AggregationsButton
              currentAggregation={activeAggregation}
              defaultAggregation={defaultTableAggregation}
              onApplyAggregation={(agg) => handleSetAggregation(agg)}
              onResetToDefault={() => handleSetAggregation(defaultTableAggregation)}
              options={INVESTMENT_AGGREGATION_DIMENSIONS}
            />
            <ViewConfig
              moduleTitle='Investment Detail'
              columns={viewConfigColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              addColumnsCatalog={addColumnsCatalog}
              addColumnsTabLabels={{ primary: 'Fund', related: 'Related' }}
              columnLayout={columnLayout}
              filterOptionsConfig={{
                panel: (
                  <InvestmentsOptionsPanel
                    value={draftOptions}
                    onChange={handleSetDraftOptions}
                  />
                ),
                isDirty: optionsIsDirty,
                isAlreadyDefault: optionsAlreadyDefault,
                onApply: handleApplyOptions,
                onCancel: handleCancelOptions,
                onResetToDefaults: handleResetOptionsToDefault
              }}
              filteringConfig={{
                currentFilters: activeFilters,
                defaultFilters: defaultTableFilters,
                onApplyFilters: async (filters) => {
                  handleSetFilters(filters)
                },
                onResetToDefaults: async () => {
                  handleSetFilters(defaultTableFilters)
                }
              }}
              sortingConfig={{
                currentSorting: activeSorting,
                defaultSorting: defaultTableSorting,
                onApplySorting: async (sorting) => {
                  handleSetSorting(sorting)
                },
                onResetToDefaults: async () => {
                  handleSetSorting(defaultTableSorting)
                }
              }}
            />
          </div>
        }
      />

      <div className='flex-1 min-h-0 h-full' style={{ minHeight: 320 }}>
        <TableView
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          currentPage={currentPage}
          currentFilters={currentFilters}
          onFiltersChange={(filters) => {
            const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
            handleSetFilters(filterArray)
          }}
          onSortChange={(sortConfig: any) => {
            if (sortConfig) {
              handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
            } else {
              handleSetSorting([])
            }
          }}
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          currentSortConfig={currentSortConfig}
          frozenColumns={frozenColumns}
          totalsMode='sticky-bottom'
          rowClassName={(record) => (recordHasTotalLabel(record) ? 'font-bold' : undefined)}
          headerStyle={{ padding: '5px', minHeight: 36, backgroundColor: '#f5f5f5' }}
        />
      </div>
    </div>
  )
}

export default InvestmentDetailTable
